/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  MutationOptions,
  QueryClient,
  QueryKey,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import type { Credentials } from '@opengeoweb/authentication';
import { useAuthenticationContext } from '@opengeoweb/authentication';

/**
 * Return type for mutations with an Error and optional optimistic-update context.
 *
 * @typeParam TData - Data returned by the mutation when it succeeds.
 * @typeParam TVariables - Input passed to mutate or mutateAsync.
 * @typeParam TContext - Value returned by onMutate and passed to callbacks,
 * such as previous data used by onError to roll back an optimistic update.
 */
export type UseMutationReturnType<
  TData = unknown,
  TVariables = void,
  TContext = unknown,
> = UseMutationResult<TData, Error, TVariables, TContext>;

export const getHeaders = (auth?: Credentials): HeadersInit =>
  omit(
    {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.token}`,
      Accept: 'application/json',
    },
    auth ? [] : ['Authorization'],
  );

export const withJsonFormat = (url: string): string => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('f', 'json');
  return urlObj.toString();
};

/**
 * Extracts the error message from a non-ok response and throws it, so the
 * backend message can be shown to the user. Uses `message` from the JSON body
 * when present, else the stringified body, else the status code. The response
 * status is set as the error `cause`.
 */
export const throwResponseError = async (
  response: Response,
): Promise<never> => {
  const json = await response.json().catch(() => null);

  if (json) {
    const { message } = json as { message?: string };
    if (message) {
      throw new Error(message, { cause: response.status });
    }
    throw new Error(JSON.stringify(json), { cause: response.status });
  }

  throw new Error(`${response.status}`, { cause: response.status });
};

// This function has a copy in libs/webmap-react/src/lib/edr-layer-api/api.ts. This cannot be used there due to circular dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleResponse = async (response: Response): Promise<any> => {
  if (response.ok) {
    return response
      .json()
      .then((json) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return json ?? response.headers;
      })
      .catch(() => {
        // No JSON response
      });
  }
  return throwResponseError(response);
};

export const useAuthQuery = <T>(
  queryKey: string[],
  queryFn: (auth: Credentials) => Promise<T>,
  disabled = false,
  refetchInterval: number | false = false,
): UseQueryResult<T> => {
  const { auth } = useAuthenticationContext();
  const enabled = !!auth && !disabled;
  return useQuery({
    queryKey,
    queryFn: async (): Promise<T> => queryFn(auth!),
    enabled,
    refetchInterval,
  });
};

export const optimisticUpdate = <
  U extends { id?: string; eventId?: string },
  T extends { id?: string; eventId?: string },
>(
  updatedQueryKey: string[],
  queryClient: QueryClient,
  optimisticUpdate: (oldValue: U, variables: T) => U,
  errorCallback?: (err: Error) => void,
): MutationOptions<
  Headers | null,
  Error,
  T,
  { previousData: [QueryKey, U[] | undefined][] }
> => ({
  onMutate: async (
    variables: T,
  ): Promise<{ previousData: [QueryKey, U[] | undefined][] }> => {
    await queryClient.cancelQueries({
      queryKey: updatedQueryKey,
      exact: false,
    });
    const previousData = queryClient.getQueriesData<U[]>({
      queryKey: updatedQueryKey,
      exact: false,
    });
    const matchesVariables = (item: U): boolean =>
      (!!variables.id && item.id === variables.id) ||
      (!!variables.eventId && item.eventId === variables.eventId);

    queryClient.setQueriesData<U[]>(
      { queryKey: updatedQueryKey, exact: false },
      (old) =>
        old?.map((item) =>
          matchesVariables(item) ? optimisticUpdate(item, variables) : item,
        ),
    );
    return { previousData };
  },
  onError: (
    err: Error,
    _: T,
    context?: { previousData: [QueryKey, U[] | undefined][] },
  ): void => {
    context?.previousData.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data);
    });
    if (errorCallback) {
      errorCallback(err);
    }
  },
});
